import React, { useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import moment from 'moment'
import { Alert } from '@mui/material'
import { useStores } from 'admin/hooks'

const WIDTH = 780
const HEIGHT = 320

const AggregatedMovement = () => {
  const { reportStore } = useStores()
  const items = reportStore.driverRouteAggregated || []
  const [hoveredNodeKey, setHoveredNodeKey] = useState(null)

  if (!items.length) {
    return (
      <Alert
        severity="info"
        sx={{ mb: 2 }}
      >
        No aggregated movement available for selected filters.
      </Alert>
    )
  }

  const isFiniteCoordinate = (value) => Number.isFinite(value)
  const isValidSegment = (segment) =>
    isFiniteCoordinate(segment?.from?.lat) &&
    isFiniteCoordinate(segment?.from?.lng) &&
    isFiniteCoordinate(segment?.to?.lat) &&
    isFiniteCoordinate(segment?.to?.lng)

  const segments = items.flatMap((item) => item.segments || []).filter(isValidSegment)

  if (!segments.length) {
    return (
      <Alert
        severity="info"
        sx={{ mb: 2 }}
      >
        No aggregated movement available for selected filters.
      </Alert>
    )
  }

  const lats = segments.flatMap((item) => [item.from.lat, item.to.lat])
  const lngs = segments.flatMap((item) => [item.from.lng, item.to.lng])
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const maxTraversals = Math.max(...segments.map((item) => item.traversals || 1))

  const mapPoint = (lat, lng) => ({
    x: ((lng - minLng) / Math.max(maxLng - minLng, 0.00001)) * (WIDTH - 40) + 20,
    y: HEIGHT - (((lat - minLat) / Math.max(maxLat - minLat, 0.00001)) * (HEIGHT - 40) + 20),
  })

  const dayNodes = useMemo(
    () =>
      items.map((dayItem) => {
        const validSegments = (dayItem.segments || []).filter(isValidSegment)
        const nodeMap = new Map()
        let unresolvedLabelCount = 0

        validSegments.forEach((segment) => {
          const endpoints = [
            { point: segment.from, area: segment.fromArea, prefix: 'from' },
            { point: segment.to, area: segment.toArea, prefix: 'to' },
          ]

          endpoints.forEach(({ point, area, prefix }) => {
            const key = point.key || `${point.lat},${point.lng}`
            const normalizedArea = typeof area === 'string' ? area.trim() : ''
            if (!normalizedArea) unresolvedLabelCount += 1
            if (!nodeMap.has(key)) {
              const mapped = mapPoint(point.lat, point.lng)
              nodeMap.set(key, {
                key,
                label: normalizedArea || 'Unknown area',
                x: mapped.x,
                y: mapped.y,
                prefix,
              })
            }
          })
        })

        const overlapCounter = new Map()
        const nodes = Array.from(nodeMap.values()).map((node) => {
          const bucketX = Math.round(node.x / 22)
          const bucketY = Math.round(node.y / 18)
          const bucketKey = `${bucketX}:${bucketY}`
          const overlapIndex = overlapCounter.get(bucketKey) || 0
          overlapCounter.set(bucketKey, overlapIndex + 1)

          const direction = overlapIndex % 2 === 0 ? 1 : -1
          const level = Math.floor(overlapIndex / 2) + 1
          const labelX = node.x + 8 + direction * level * 5
          const labelY = node.y - 10 + direction * level * 8

          return {
            ...node,
            labelX,
            labelY,
          }
        })

        return {
          date: dayItem.date,
          nodes,
          unresolvedLabelCount,
        }
      }),
    [items],
  )

  const dayNodesByDate = new Map(dayNodes.map((item) => [item.date, item]))

  return (
    <>
      <div style={{ marginBottom: '0.75rem', fontSize: 12, color: '#3f3f3f', lineHeight: 1.5 }}>
        <strong>Legend:</strong> line width = traversals, opacity = frequency, nodes = area
        centroids. Hover tooltips provide secondary segment details.
      </div>
      {items.map((dayItem) => {
        const dayNodeData = dayNodesByDate.get(dayItem.date) || {
          nodes: [],
          unresolvedLabelCount: 0,
        }

        return (
          <div
            key={dayItem.date}
            style={{ marginBottom: '1rem' }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.5rem' }}>
              {moment(dayItem.date).format('ddd, DD MMM YYYY')}
            </div>
            <svg
              width="100%"
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              style={{ border: '1px solid #ddd' }}
            >
              {(dayItem.segments || []).filter(isValidSegment).map((item) => {
                const from = mapPoint(item.from.lat, item.from.lng)
                const to = mapPoint(item.to.lat, item.to.lng)
                const weight = 1 + (item.traversals / maxTraversals) * 7
                const fromNodeKey = item.from.key || `${item.from.lat},${item.from.lng}`
                const toNodeKey = item.to.key || `${item.to.lat},${item.to.lng}`
                const isHighlighted =
                  hoveredNodeKey && (hoveredNodeKey === fromNodeKey || hoveredNodeKey === toNodeKey)

                return (
                  <line
                    key={`${dayItem.date}-${fromNodeKey}-${toNodeKey}-${item.traversals}-${item.uniqueDrivers}`}
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke="#1976d2"
                    strokeOpacity={
                      isHighlighted ? 0.95 : 0.25 + item.traversals / (maxTraversals * 1.2)
                    }
                    strokeWidth={isHighlighted ? weight + 1.5 : weight}
                  >
                    <title>{`Date: ${dayItem.date}\nTraversals: ${item.traversals}\nUnique drivers: ${item.uniqueDrivers}\nTime-window coverage: ${(item.timeWindowCoverage || []).join(', ')}\nFrom area: ${item.fromArea || 'Unknown'}\nTo area: ${item.toArea || 'Unknown'}`}</title>
                  </line>
                )
              })}
              {dayNodeData.nodes.map((node) => {
                const isHighlighted = hoveredNodeKey === node.key
                return (
                  <g
                    key={`${dayItem.date}-node-${node.key}`}
                    onMouseEnter={() => setHoveredNodeKey(node.key)}
                    onMouseLeave={() => setHoveredNodeKey(null)}
                    style={{ cursor: 'default' }}
                  >
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={isHighlighted ? 5 : 4}
                      fill={isHighlighted ? '#ef6c00' : '#0d47a1'}
                      stroke="#fff"
                      strokeWidth={1.3}
                    />
                    <text
                      x={node.labelX}
                      y={node.labelY}
                      fontSize={10}
                      fill={isHighlighted ? '#bf360c' : '#37474f'}
                      fontWeight={isHighlighted ? 700 : 500}
                    >
                      {node.label}
                    </text>
                  </g>
                )
              })}
            </svg>
            {dayNodeData.unresolvedLabelCount > 0 && (
              <Alert
                severity="warning"
                sx={{ mt: 1 }}
              >
                Some node labels could not be resolved from fromArea/toArea for this day. Nodes are
                still shown using fallback labels.
              </Alert>
            )}
            {dayNodeData.nodes.length === 0 && (
              <Alert
                severity="info"
                sx={{ mt: 1 }}
              >
                No mappable nodes available for this day.
              </Alert>
            )}
          </div>
        )
      })}
    </>
  )
}

export default observer(AggregatedMovement)
